import { t } from "i18next";
import { Fragment, JSX, useMemo } from "react";

import { format } from "date-fns";

import Typography from "@mui/material/Typography";

import { toDateString } from "utils/DateUtils";
import { asTitle } from "utils/Utils";

import { Widget } from "core/blueprint/dashboard/DashboardTypes";
import {
	ACHIEVED_COLOUR,
	isOnOrBefore,
	ResolvedScore,
	resolveScore,
	scoreOf
} from "evaluation/EffectiveScore";
import { Cluster, Criteria, CriteriaType } from "trajectory/TrajectoryService";

import { WidgetViewProps } from "./WidgetView";

export type EvaluationHistoryWidgetViewProps = WidgetViewProps<Widget>;

// One history row. A null score is a "behaald" (achieved or offered) snapshot
// without a numeric score.
type Evaluation = {
	date: string;
	score: number | null;
};

type Entry = {
	index: string;
	name: string;
	// The competence's resolved state; null for cluster rows.
	resolved: ResolvedScore | null;
	evaluations: Array<Evaluation>;
};

type Swatch = {
	colour: string;
	name: string;
};

export function EvaluationHistoryWidgetView(
	props: Readonly<EvaluationHistoryWidgetViewProps>
): JSX.Element {
	const overview: Array<Entry> = useMemo(() => {
		return level1(props.evaluation.criteria);
	}, [
		props.evaluation.criteria,
		props.evaluation.scoreResolution,
		props.asOf
	]);

	function level1(criteria: Array<Criteria>): Array<Entry> {
		const entries: Array<Entry> = [];
		for (let index = 0; index < criteria.length; index++) {
			const criterion = criteria[index];
			if (criterion.type !== CriteriaType.CLUSTER) {
				continue;
			}

			const indexString = (index + 1).toString();
			const subEntries = level2(
				indexString,
				(criterion as Cluster).subsections
			);

			if (subEntries.length > 0) {
				entries.push({
					index: indexString,
					name: criterion.name,
					resolved: null,
					evaluations: []
				});

				entries.push(...subEntries);
			}
		}

		return entries;
	}

	function level2(prefix: string, criteria: Array<Criteria>): Array<Entry> {
		const entries: Array<Entry> = [];
		for (let index = 0; index < criteria.length; index++) {
			const criterion = criteria[index];
			if (criterion.type !== CriteriaType.CLUSTER) {
				continue;
			}

			const indexString = (index + 1).toString();
			const subEntries = level3(
				`${prefix}.${indexString}`,
				(criterion as Cluster).subsections
			);

			if (subEntries.length > 0) {
				entries.push({
					index: `${prefix}.${indexString}`,
					name: criterion.name,
					resolved: null,
					evaluations: []
				});

				entries.push(...subEntries);
			}
		}

		return entries;
	}

	function level3(prefix: string, criteria: Array<Criteria>): Array<Entry> {
		const entries: Array<Entry> = [];
		for (let index = 0; index < criteria.length; index++) {
			const criterion = criteria[index];

			// Scored snapshots and score-less "behaald" snapshots are rows,
			// like in the PDF listing.
			const asOf = props.asOf;
			const evaluations: Evaluation[] = Object.entries(
				criterion.properties ?? {}
			)
				.filter(
					([date, snapshot]) =>
						asOf === undefined ||
						isOnOrBefore(date, snapshot, asOf)
				)
				.filter(
					([, snapshot]) =>
						scoreOf(snapshot) !== undefined ||
						snapshot.achieved === "true" ||
						snapshot.offered === "true"
				)
				.map(([date, snapshot]) => ({
					date: snapshot.date ?? toDateString(date),
					score: scoreOf(snapshot) ?? null
				}));

			// A competence is listed as soon as it has one such row, so
			// never-scored achieved competences show up too.
			if (evaluations.length === 0) {
				continue;
			}

			entries.push({
				index: `${prefix}.${(index + 1).toString()}`,
				name: criterion.name,
				resolved: resolveScore(
					criterion,
					props.evaluation.scoreResolution,
					props.asOf
				),
				evaluations: evaluations
			});
		}

		return entries;
	}

	// Colour and name for a score, or for "behaald" when there is none.
	function describe(score: number | null): Swatch {
		if (score === null) {
			return { colour: ACHIEVED_COLOUR, name: asTitle(t("achieved")) };
		}

		const scoreIndex = score - props.evaluation.minScore;
		return {
			colour: props.evaluation.scoreColours[scoreIndex],
			name: props.evaluation.scoreNames[scoreIndex]
		};
	}

	// The competence's current state under the trajectory's score resolution.
	function resolvedSwatch(resolved: ResolvedScore | null): Swatch | null {
		if (resolved === null || resolved.kind === "none") {
			return null;
		}

		return describe(resolved.kind === "score" ? resolved.score : null);
	}

	function renderSwatch(swatch: Swatch): JSX.Element {
		return (
			<div
				style={{
					width: "17.5em",
					display: "flex"
				}}>
				<div
					style={{
						width: "1em",
						height: "1em",
						marginLeft: "1em",
						marginRight: "0.5em",
						border: "1px solid black",
						backgroundColor: swatch.colour
					}}
				/>
				<div
					style={{
						width: "15em"
					}}>
					<Typography variant="body2">{swatch.name}</Typography>
				</div>
			</div>
		);
	}

	return (
		<>
			<Typography variant="h2" align="center">
				{props.widget.name}
			</Typography>
			{props.asOf !== undefined && (
				<Typography variant="subtitle1" align="center">
					{asTitle(t("asOf"))} {format(props.asOf, "dd/MM/yyyy")}
				</Typography>
			)}
			<div
				style={{
					width: "70%",
					margin: "auto",
					display: "flex",
					flexDirection: "column"
				}}>
				{overview.map(entry => {
					const swatch = resolvedSwatch(entry.resolved);
					return (
						<Fragment key={entry.index}>
							<div style={{ display: "flex" }}>
								<div style={{ width: "3em" }}>
									<Typography variant="subtitle2">
										{entry.index}
									</Typography>
								</div>
								<div style={{ flexGrow: 1 }}>
									<Typography variant="subtitle2">
										{entry.name}
									</Typography>
								</div>
								{swatch !== null && renderSwatch(swatch)}
							</div>

							<div>
								{entry.evaluations.length > 0 && (
									<div>
										<div style={{ textAlign: "center" }}>
											<Typography variant="subtitle2">
												{asTitle(t("evaluation_other"))}
											</Typography>
										</div>

										{entry.evaluations.map(
											(evaluation, index) => (
												<div
													key={index}
													style={{
														display: "flex",
														justifyContent:
															"space-between"
													}}>
													<div
														style={{
															marginLeft: "3em",
															flexGrow: 1
														}}>
														<Typography variant="body2">
															{evaluation.date}
														</Typography>
													</div>
													{renderSwatch(
														describe(
															evaluation.score
														)
													)}
												</div>
											)
										)}
									</div>
								)}
							</div>
						</Fragment>
					);
				})}
			</div>
		</>
	);
}
